import axios from "axios";
import { fetchAuthSession } from "@aws-amplify/auth";
import awsconfig from "@/aws-exports";
import Vue from "vue";
import {
  handleSessionExpiry,
  isSessionExpiryError,
} from "@/modules/session-expiry";

const axiosService = axios.create({
  timeout: 120000,
});

let tokenRefreshPromise = null;

function getAccessToken(session) {
  const token = session?.tokens?.accessToken?.toString();
  if (!token) {
    const error = new Error("The authentication session has no access token.");
    error.name = "UserUnAuthenticatedException";
    throw error;
  }
  return token;
}

async function refreshAuthSession(authorization) {
  if (!tokenRefreshPromise) {
    tokenRefreshPromise = (async () => {
      const session = await fetchAuthSession();
      // A delayed 401 may belong to a token that has already been refreshed.
      if ("Bearer " + getAccessToken(session) !== authorization) {
        return session;
      }
      const refreshedSession = await fetchAuthSession({ forceRefresh: true });
      getAccessToken(refreshedSession);
      return refreshedSession;
    })().finally(() => {
      tokenRefreshPromise = null;
    });
  }

  return tokenRefreshPromise;
}

// Add a request interceptor
axiosService.interceptors.request.use(
  async function (config) {
    try {
      // Requests arriving while a forced refresh is running must wait for the
      // same refresh instead of being sent with the previous access token.
      const session = tokenRefreshPromise
        ? await tokenRefreshPromise
        : await fetchAuthSession();
      const userPoolUserToken = getAccessToken(session);

      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${userPoolUserToken}`,
        "Content-Type": "application/json",
      };
      config.baseURL = awsconfig.API.endpoints[0].endpoint;
      return config;
    } catch (error) {
      if (isSessionExpiryError(error)) {
        await handleSessionExpiry();
      }
      return Promise.reject(error);
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor
axiosService.interceptors.response.use(
  (response) => {
    if (response.config?.responseType === "blob") {
      return response;
    }

    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._authRetry
    ) {
      originalRequest._authRetry = true;

      try {
        await refreshAuthSession(originalRequest.headers?.Authorization);
        return axiosService(originalRequest);
      } catch (refreshError) {
        if (isSessionExpiryError(refreshError)) {
          await handleSessionExpiry();
        }
        return Promise.reject(refreshError);
      }
    }

    switch (error.response?.status) {
      case 400:
        Vue.notify({
          group: "error",
          title: "Hibás lekérdezés!",
          text: "A kért adatok lekérdezése közben hiba történt.",
        });
        break;
      case 401:
        await handleSessionExpiry();
        break;
      case 403:
        Vue.notify({
          group: "error",
          title: "Tiltott lekérdezés!",
          text: "Nem rendelkezik érvényes hitelesítési adatokkal.",
        });
        break;
      case 404:
        Vue.notify({
          group: "error",
          title: "Nem található!",
          text: "A kért oldal nem található.",
        });
        break;
      case 500:
        Vue.notify({
          group: "error",
          title: "Nem érkeztek friss számlainformációk!",
          text: "Kérjük próbálkozzon újra később.",
        });
    }
    return Promise.reject(error);
  }
);

export default axiosService;
